import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { googleSignIn } from "../lib/googleSignIn";
import { EvoluraLogo } from "../components/EvoluraLogo";
import SEO from "../components/SEO";
import { fbPageView } from "../utils/fbpixel";

const API = "/api";

/* ─── Orbital People Data ─── */
const ORBIT_PEOPLE = [
  { name: "Marcus", role: "old mentor", angle: 210, orbit: 1, size: 22, img: "https://images.unsplash.com/photo-1679476819592-3e233227fd83?w=120&h=120&fit=crop&crop=face" },
  { name: "Priya", role: "your sister", angle: 35, orbit: 2, size: 20, img: "https://images.unsplash.com/photo-1585477212852-91dc0f1bba61?w=120&h=120&fit=crop&crop=face" },
  { name: "Dad", role: "", angle: 330, orbit: 1, size: 24, img: "https://images.unsplash.com/photo-1752070288166-057f014d248f?w=120&h=120&fit=crop&crop=face" },
  { name: "Sam", role: "college friend", angle: 250, orbit: 2, size: 18, img: "https://images.unsplash.com/photo-1675756235174-18396273b879?w=120&h=120&fit=crop&crop=face" },
];

const YOU_IMG = "https://images.unsplash.com/photo-1581322929625-f4aab333778a?w=120&h=120&fit=crop&crop=face";

/* ─── Orbital Visualization ─── */
const OrbitalDiagram = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 50);
    return () => clearInterval(id);
  }, []);

  const cx = 280, cy = 260;
  const orbits = [160, 235];

  return (
    <svg viewBox="0 0 560 520" className="w-full h-full" data-testid="orbital-diagram">
      <defs>
        <radialGradient id="glow-center" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#c4b5fd" stopOpacity="0" />
        </radialGradient>
        <filter id="dot-glow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Clip paths for circular photos */}
        <clipPath id="clip-you"><circle cx={cx} cy={cy} r="28" /></clipPath>
        {ORBIT_PEOPLE.map((_, i) => (
          <clipPath key={i} id={`clip-person-${i}`}><circle cx="0" cy="0" r="1" /></clipPath>
        ))}
      </defs>

      {/* Background glow */}
      <circle cx={cx} cy={cy} r="180" fill="url(#glow-center)" />

      {/* Dotted orbital lines — large, prominent, overlapping */}
      {/* Orbit 1: inner, tilted left */}
      <ellipse cx={cx} cy={cy} rx="140" ry="200" fill="none"
        stroke="#ffffff" strokeOpacity="0.1" strokeWidth="1" strokeDasharray="3 8"
        transform={`rotate(-25, ${cx}, ${cy})`} />
      {/* Orbit 2: mid, tilted right */}
      <ellipse cx={cx} cy={cy} rx="190" ry="260" fill="none"
        stroke="#ffffff" strokeOpacity="0.08" strokeWidth="1" strokeDasharray="3 8"
        transform={`rotate(20, ${cx}, ${cy})`} />
      {/* Orbit 3: outer, nearly vertical */}
      <ellipse cx={cx} cy={cy} rx="120" ry="300" fill="none"
        stroke="#ffffff" strokeOpacity="0.06" strokeWidth="1" strokeDasharray="3 10"
        transform={`rotate(60, ${cx}, ${cy})`} />

      {/* Center: YOU */}
      <circle cx={cx} cy={cy} r="30" fill="#c4b5fd" fillOpacity="0.15" filter="url(#dot-glow)" />
      <circle cx={cx} cy={cy} r="28" fill="none" stroke="#c4b5fd" strokeWidth="2" strokeOpacity="0.5" />
      <image
        href={YOU_IMG}
        x={cx - 28} y={cy - 28}
        width="56" height="56"
        clipPath="url(#clip-you)"
        preserveAspectRatio="xMidYMid slice"
      />
      <text
        x={cx} y={cy + 44}
        textAnchor="middle"
        fill="#ffffff"
        fontSize="10"
        fontWeight="500"
        letterSpacing="3"
        fontFamily="system-ui, sans-serif"
      >
        YOU
      </text>

      {/* People */}
      {ORBIT_PEOPLE.map((p, i) => {
        const r = orbits[p.orbit - 1];
        const ry = r * 0.55;
        const rotation = (-15 + (p.orbit - 1) * 12) * Math.PI / 180;
        const baseAngle = (p.angle + tick * (0.08 + i * 0.02)) * Math.PI / 180;

        const ex = r * Math.cos(baseAngle);
        const ey = ry * Math.sin(baseAngle);
        const px = cx + ex * Math.cos(rotation) - ey * Math.sin(rotation);
        const py = cy + ex * Math.sin(rotation) + ey * Math.cos(rotation);

        const sz = p.size;
        const labelRight = px > cx;

        return (
          <g key={i}>
            {/* Glow ring */}
            <circle cx={px} cy={py} r={sz + 3} fill="#b794f6" fillOpacity="0.12" filter="url(#dot-glow)" />
            <circle cx={px} cy={py} r={sz} fill="none" stroke="#b794f6" strokeWidth="1.5" strokeOpacity="0.5" />
            {/* Photo */}
            <clipPath id={`orbit-clip-${i}`}>
              <circle cx={px} cy={py} r={sz} />
            </clipPath>
            <image
              href={p.img}
              x={px - sz} y={py - sz}
              width={sz * 2} height={sz * 2}
              clipPath={`url(#orbit-clip-${i})`}
              preserveAspectRatio="xMidYMid slice"
            />
            {/* Label */}
            <text
              x={px + (labelRight ? sz + 8 : -(sz + 8))}
              y={py + 4}
              textAnchor={labelRight ? "start" : "end"}
              fill="#d4c5f9"
              fontSize="11"
              fontStyle="italic"
              fontFamily="system-ui, sans-serif"
              opacity="0.85"
            >
              {p.name}
            </text>
            {p.role && (
              <text
                x={px + (labelRight ? sz + 8 : -(sz + 8))}
                y={py + 17}
                textAnchor={labelRight ? "start" : "end"}
                fill="#8b7bac"
                fontSize="9"
                fontFamily="system-ui, sans-serif"
                opacity="0.7"
              >
                {p.role}
              </text>
            )}
          </g>
        );
      })}

      {/* Ambient dots */}
      {[
        { x: 100, y: 130, s: 2.5 },
        { x: 460, y: 100, s: 2 },
        { x: 400, y: 440, s: 1.5 },
        { x: 70, y: 370, s: 2 },
      ].map((d, i) => (
        <circle key={`tiny-${i}`} cx={d.x} cy={d.y} r={d.s} fill="#b794f6" fillOpacity="0.2" />
      ))}
    </svg>
  );
};

/* ═══════════════════ LANDING PAGE B ═══════════════════ */
const LandingPageB = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [inviteToken, setInviteToken] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("invite_token");
    if (token) setInviteToken(token);
    fbPageView();
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [loading, user, navigate]);

  const handleSignIn = () => {
    const returnUrl = sessionStorage.getItem("pending_return_url") || "";
    googleSignIn({ inviteToken: inviteToken || undefined, returnUrl: returnUrl || undefined });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0c0a1d" }}>
        <div className="w-8 h-8 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: "radial-gradient(ellipse at 60% 50%, #1a1440 0%, #0c0a1d 60%, #080714 100%)" }}
      data-testid="landing-page-b"
    >
      <SEO
        title="See yourself through the eyes of people who know you"
        description="Invite your mentors, friends, and family to share honest feedback. Personal SWOT, Core Values, and Wheel of Life tools for self-discovery."
        path="/welcome"
      />

      {/* Ambient glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: "800px", height: "800px",
          left: "40%", top: "10%",
          background: "radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)",
          transform: "translate(-50%, -20%)",
        }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-16 pt-8">
        <EvoluraLogo size={28} variant="light" />
        <button
          onClick={handleSignIn}
          className="text-sm text-white/50 hover:text-white/80 transition-colors hidden sm:block"
          data-testid="header-signin"
        >
          Sign in
        </button>
      </header>

      {/* Hero */}
      <main className="relative z-10 flex flex-col lg:flex-row items-center lg:items-start min-h-[calc(100vh-80px)] px-6 sm:px-10 lg:px-16 pt-12 sm:pt-20 lg:pt-32">

        {/* Left: Copy */}
        <div className="flex-1 max-w-xl lg:max-w-2xl">
          <h1 className="leading-[1.05] tracking-tight">
            <span
              className="block text-white text-4xl sm:text-5xl lg:text-[64px] font-light"
              style={{ fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif" }}
            >
              Three people already
            </span>
            <span
              className="block text-4xl sm:text-5xl lg:text-[64px] font-light italic mt-1"
              style={{
                fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif",
                color: "#c4b5fd",
              }}
            >
              see what you can't.
            </span>
          </h1>

          <p
            className="mt-6 sm:mt-8 text-base sm:text-lg leading-relaxed max-w-md"
            style={{ color: "#b8b0cc" }}
          >
            Invite the ones who know you. See yourself the way
            they do &mdash; with their honesty, not yours.
          </p>

          {/* CTA */}
          <div className="mt-8 sm:mt-10 flex items-center gap-4 flex-wrap">
            <button
              onClick={handleSignIn}
              className="flex items-center gap-3 px-6 py-3.5 rounded-2xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-violet-500/10 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "rgba(255,255,255,0.95)",
                color: "#1a1a2e",
              }}
              data-testid="cta-signin"
            >
              {/* Google G icon */}
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92a8.78 8.78 0 002.68-6.62z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A8.99 8.99 0 009 18z" fill="#34A853"/>
                <path d="M3.97 10.71A5.41 5.41 0 013.68 9c0-.6.1-1.17.29-1.71V4.96H.96A8.99 8.99 0 000 9c0 1.45.35 2.82.96 4.04l3.01-2.33z" fill="#FBBC05"/>
                <path d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 00.96 4.96l3.01 2.33C4.68 5.17 6.66 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Sign in to invite your circle
            </button>
            <span className="text-xs" style={{ color: "#7c7394" }}>
              Takes 30 seconds.
            </span>
          </div>

          {/* Social proof */}
          <div className="mt-8 flex items-center gap-3" data-testid="social-proof">
            <div className="flex -space-x-2">
              {[
                "https://images.unsplash.com/photo-1614700308520-bd630ab82471?w=40&h=40&fit=crop&crop=face",
                "https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?w=40&h=40&fit=crop&crop=face",
                "https://images.unsplash.com/photo-1697063882499-f7fca7d2d713?w=40&h=40&fit=crop&crop=face",
                "https://images.unsplash.com/photo-1582070595814-fe36a8d39532?w=40&h=40&fit=crop&crop=face",
              ].map((src, i) => (
                <img key={i} src={src} alt="" className="w-7 h-7 rounded-full border-2 object-cover" style={{ borderColor: "#0c0a1d" }} />
              ))}
            </div>
            <p className="text-xs" style={{ color: "#8b7bac" }}>
              <span style={{ color: "#c4b5fd" }} className="font-medium">200+ people</span> discovering their blind spots
            </p>
          </div>

          {/* Try tools — prominent cards */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="try-tools-cards">
            {[
              { href: "/swot", label: "SWOT Analysis", desc: "Map your strengths & blind spots" },
              { href: "/values", label: "Core Values", desc: "Build your identity pyramid" },
              { href: "/wheel", label: "Wheel of Life", desc: "Rate 10 areas of your life" },
            ].map(tool => (
              <a
                key={tool.href}
                href={tool.href}
                className="group flex-1 px-4 py-3 rounded-xl border transition-all hover:scale-[1.02]"
                style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}
                data-testid={`try-card-${tool.href.slice(1)}`}
              >
                <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">{tool.label}</span>
                <span className="block text-[11px] mt-0.5" style={{ color: "#7c7394" }}>{tool.desc}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Right: Orbital Diagram */}
        <div className="flex-1 max-w-lg lg:max-w-xl w-full mt-8 lg:mt-0 lg:ml-8">
          <OrbitalDiagram />
        </div>
      </main>

      {/* Scroll indicator */}
      <div className="relative z-10 flex justify-center pb-6 animate-bounce" data-testid="scroll-indicator">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4 7l6 6 6-6" stroke="#7c7394" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* ═══════ Below the fold: Value Proposition ═══════ */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 py-20 sm:py-28" data-testid="value-section">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-light text-center mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#e2ddf0" }}
          >
            How it works
          </h2>
          <p className="text-center text-sm mb-16 max-w-lg mx-auto" style={{ color: "#7c7394" }}>
            Three tools. Real people. A clearer picture of who you are.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                num: "01",
                title: "Get a SWOT from your circle",
                desc: "Fill in your strengths and weaknesses, then invite friends, family, and mentors to add what they see. Their suggestions show up alongside yours.",
                color: "#a78bfa",
              },
              {
                num: "02",
                title: "Discover your core values",
                desc: "Choose from 50 values and 50 anti-values to build your identity pyramid. See which values the people around you recognize in you.",
                color: "#c4b5fd",
              },
              {
                num: "03",
                title: "See where life needs attention",
                desc: "Rate 10 areas of your life with the Wheel of Life. Link insights from your SWOT directly into each area to create a real action plan.",
                color: "#e9d5ff",
              },
            ].map(step => (
              <div key={step.num} className="text-center md:text-left">
                <span
                  className="text-4xl font-light block mb-4"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif", color: step.color, opacity: 0.4 }}
                >
                  {step.num}
                </span>
                <h3 className="text-base font-medium text-white mb-2">{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#8b7bac" }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ Testimonials / Social Proof Section ═══════ */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 py-16 sm:py-20" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "My best friend told me I was a great listener — I never thought of it as a strength until I saw it in my SWOT.",
                name: "Camila R.",
                role: "Product Designer",
              },
              {
                quote: "I asked 4 people. Two of them pointed out the same blind spot. That was the wake-up call I needed.",
                name: "James K.",
                role: "Engineering Lead",
              },
              {
                quote: "The values pyramid made me realize I was chasing things I don't actually care about. Clarity in 10 minutes.",
                name: "Ananya P.",
                role: "Startup Founder",
              },
            ].map((t, i) => (
              <div
                key={i}
                className="p-5 rounded-2xl border"
                style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}
                data-testid={`testimonial-${i}`}
              >
                <p className="text-sm leading-relaxed italic mb-4" style={{ color: "#b8b0cc" }}>
                  "{t.quote}"
                </p>
                <div>
                  <span className="text-xs font-medium text-white/80">{t.name}</span>
                  <span className="text-xs ml-2" style={{ color: "#7c7394" }}>{t.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ Final CTA ═══════ */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 py-16 sm:py-24" data-testid="final-cta">
        <div className="max-w-lg mx-auto text-center">
          <h2
            className="text-2xl sm:text-3xl font-light mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#e2ddf0" }}
          >
            Ready to see what they see?
          </h2>
          <p className="text-sm mb-8" style={{ color: "#7c7394" }}>
            It starts with one invite. The insights follow.
          </p>
          <button
            onClick={handleSignIn}
            className="inline-flex items-center gap-3 px-7 py-4 rounded-2xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-violet-500/10 hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: "rgba(255,255,255,0.95)", color: "#1a1a2e" }}
            data-testid="final-cta-button"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92a8.78 8.78 0 002.68-6.62z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A8.99 8.99 0 009 18z" fill="#34A853"/>
              <path d="M3.97 10.71A5.41 5.41 0 013.68 9c0-.6.1-1.17.29-1.71V4.96H.96A8.99 8.99 0 000 9c0 1.45.35 2.82.96 4.04l3.01-2.33z" fill="#FBBC05"/>
              <path d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 00.96 4.96l3.01 2.33C4.68 5.17 6.66 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Discover your blind spots
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 sm:px-10 lg:px-16 pb-8 pt-4">
        <p className="text-[11px]" style={{ color: "#4a4260" }}>
          Evoluras &middot; Personal growth, through the eyes of others.
          {" · "}
          <Link to="/privacy" className="hover:underline" style={{ color: "#6e6188" }} data-testid="footer-privacy-link">Privacy</Link>
        </p>
      </footer>

      {/* Playfair Display font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,300;0,400;1,300;1,400&display=swap"
        rel="stylesheet"
      />
    </div>
  );
};

export default LandingPageB;
